import { Controller } from '@hotwired/stimulus';
import tippy from 'tippy.js';

const SVG_PATH = typeof AppConfig !== 'undefined' ? AppConfig.templateUrl : '..';

const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

const normalizeSvgId = (id) => String(id.replaceAll()).replaceAll('._', '.');

const getTooltipContent = (detailEl) => {
	if (!detailEl) return;
	const { tooltipName, tooltipDisposition, tooltipArea, tooltipPrice, tooltipStatus } = detailEl.dataset;

	return `
		<div class="floorplan__tooltip">
			<div class="floorplan__tooltip-name">${tooltipName}</div>
			<div class="floorplan__tooltip-bottom">
				<div class="floorplan__tooltip-data">
					${tooltipDisposition}<br />
					${tooltipArea.split('.')[0]}&nbsp;m²
				</div>
			</div>
			<div class="floorplan__tooltip-status">${tooltipStatus ? tooltipStatus : tooltipPrice}</div>
		</div>
	`;
};

export const create = () => {
	return class extends Controller {
		static targets = ['plan', 'detail'];
		static values = {
			building: String,
			floor: String,
		};

		initialize() {
			this.armedLinks = new WeakMap();
		}

		async connect() {
			await this.renderSvg();
			const polygons = this.planTarget.querySelectorAll('path, polygon, polyline, rect');
			const filteredPolygons = [...polygons].filter((el) => el.hasAttribute('id'));

			Array.from(filteredPolygons).forEach((polygon) => {
				const group = polygon;
				const polygonDetail = this.detailTargets.find((detail) => detail.id === `detail-${normalizeSvgId(group.id)}`);

				if (polygonDetail) {
					const url = polygonDetail.dataset.tooltipUrl;
					const name = polygonDetail.dataset.tooltipName;

					if (url && url.trim() !== '') {
						const svgNS = 'http://www.w3.org/2000/svg';
						const xlinkNS = 'http://www.w3.org/1999/xlink';

						const link = document.createElementNS(svgNS, 'a');
						link.setAttributeNS(xlinkNS, 'xlink:href', url);
						link.setAttribute('target', '_self');

						const parent = polygon.parentNode;
						parent.insertBefore(link, polygon);
						link.appendChild(polygon);
						link.setAttribute('data-action', 'click->floorplan#handleLinkClick');

						if (name && name.trim() !== '') {
							const span = document.createElement('span');
							span.classList.add('u-vhide');
							span.textContent = name;
							link.appendChild(span);
						}
					}

					polygon.classList.add('floorplan__polygon');

					if (polygonDetail.dataset.tooltipStatus) {
						polygon.classList.add('floorplan__polygon--empty');
					}

					if (polygonDetail.dataset.tooltipState) {
						polygon.classList.add('floorplan__polygon--' + polygonDetail.dataset.tooltipState);

						if (polygonDetail.dataset.tooltipState === 'in-preparation') {
							polygon.classList.remove('floorplan__polygon--empty');
						}
					}

					tippy(polygon, {
						content: getTooltipContent(polygonDetail),
						placement: 'top-start',
						arrow: false,
						allowHTML: true,
						theme: 'floorplan',
						duration: [100, 0],
					});
				}
			});
		}

		handleClick(event) {
			const target = event.currentTarget;
			const group = target.closest('g[id]');
			const polygons = this.planTarget.querySelectorAll('polygon, polyline');

			const newActiveDetail = this.detailTargets.find((detail) => detail.id === `detail-${normalizeSvgId(group.id)}`);

			if (!newActiveDetail) return;

			polygons.forEach((pol) => pol.classList.remove('active'));
			this.detailTargets.forEach((detail) => detail.classList.remove('active'));
			target.classList.add('active');
			newActiveDetail.classList.add('active');
		}
		handleLinkClick(event) {
			if (!isTouchDevice) return;

			const clickedLink = event.currentTarget;

			if (!this.armedLinks.get(clickedLink)) {
				event.preventDefault();

				this.planTarget.querySelectorAll('a[data-action*="handleLinkClick"]').forEach((link) => {
					this.armedLinks.set(link, false);
				});

				this.armedLinks.set(clickedLink, true);
			}
		}

		async renderSvg() {
			try {
				if (!this.buildingValue || !this.floorValue) return;
				const res = await fetch(
					SVG_PATH +
						`/img/floorplans/PSN_Brno_Jedna_navigator_floorplans_${this.buildingValue}_${this.floorValue.toLowerCase()}NP.svg`,
				);

				if (res.ok && res.status === 200) {
					this.planTarget.innerHTML = await res.text();
					this.planTarget.classList.remove('is-loading');
				}
			} catch (e) {
				console.error(e);
			}
		}
	};
};
